import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/utils/tailwind';

const buttonVariants = cva(
  'inline-flex items-center  justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gr-300 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-[#8276FC] text-white shadow-sm hover:bg-[#8276FC]/90 ',
        muted: 'bg-gr-100 text-gr-700 shadow-sm hover:bg-[#F3F3F9]/90',
        destructive: 'bg-[#E02626] text-white shadow-sm hover:bg-[#E02626]/90',
        outline: 'border border-gr-300 bg-white shadow-sm hover:bg-gr-100 hover:text-gr-900',
        secondary: 'bg-gr-200 text-gr-900 shadow-sm hover:bg-gr-300',
        ghost: 'hover:bg-gr-100 hover:text-gr-900',
        link: 'text-[#FF7F00] underline-offset-4 hover:underline',
      },
      size: {
        default: 'px-[18px] py-[8px] Body5M14',
        secondary: 'px-[10px] py-[6px] Body5M14',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-4 w-4 p-5',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
